import React,{useMemo,useState}from"react";
import{Autocomplete,Box,Button,CircularProgress,IconButton,MenuItem,Paper,Stack,TextField,Typography}from"@mui/material";
import AddRoundedIcon from"@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from"@mui/icons-material/DeleteOutlineRounded";
import Persons from"../AdminPerson/Persons";
import{useDashboardTheme}from"../../../context/DashboardThemeContext";

function money(value){
  const n=Number(value||0);
  return Number.isNaN(n)?"0.00":n.toFixed(2);
}

function round2(value){
  return Math.round((Number(value||0)+Number.EPSILON)*100)/100;
}

function parseAmount(value){
  const number=Number(String(value??"").replace(",",".").trim());
  return Number.isFinite(number)?number:0;
}

function getDocumentoText(option){
  if(!option)return"";
  if(option.documento_tipo==="RUC"&&option.documento_valor)return`RUC: ${option.documento_valor}`;
  if(option.documento_tipo==="CEDULA"&&option.documento_valor)return`Cédula: ${option.documento_valor}`;
  if(option.ruc)return`RUC: ${option.ruc}`;
  if(option.cedula)return`Cédula: ${option.cedula}`;
  return"";
}

function getPersonName(option){
  if(!option)return"";
  return option.razon_social||option.nombre_comercial||"";
}

function getPersonLabel(option){
  if(!option)return"";
  const documento=getDocumentoText(option);
  const name=getPersonName(option);
  if(documento&&name)return`${documento} - ${name}`;
  if(documento)return documento;
  return name;
}

function resolveDefaultDocumento(persona){
  if(!persona)return{documento_tipo:"",documento_valor:""};
  if(persona.ruc)return{documento_tipo:"RUC",documento_valor:persona.ruc};
  if(persona.documento_tipo==="RUC"&&persona.documento_valor)return{documento_tipo:"RUC",documento_valor:persona.documento_valor};
  if(persona.cedula)return{documento_tipo:"CEDULA",documento_valor:persona.cedula};
  if(persona.documento_tipo==="CEDULA"&&persona.documento_valor)return{documento_tipo:"CEDULA",documento_valor:persona.documento_valor};
  return{documento_tipo:"",documento_valor:""};
}

function normalizePerson(persona){
  if(!persona)return null;
  const documento=resolveDefaultDocumento(persona);
  return{
    ...persona,
    cedula:documento.documento_tipo==="CEDULA"?documento.documento_valor:"",
    ruc:documento.documento_tipo==="RUC"?documento.documento_valor:"",
    documento_tipo:documento.documento_tipo,
    documento_valor:documento.documento_valor,
    option_uid:`${persona.id}-${documento.documento_tipo||"SIN"}`
  };
}

function fieldSx(dashboardTheme){
  return{"& .MuiOutlinedInput-root":{borderRadius:"8px",color:dashboardTheme.text_color,bgcolor:dashboardTheme.input_background||dashboardTheme.card_background,"& fieldset":{borderColor:dashboardTheme.border_color},"&:hover fieldset":{borderColor:dashboardTheme.primary_color},"&.Mui-focused fieldset":{borderColor:dashboardTheme.primary_color}},"& .MuiInputLabel-root":{color:dashboardTheme.muted_text_color},"& .MuiInputLabel-root.Mui-focused":{color:dashboardTheme.primary_color},"& .MuiSvgIcon-root":{color:dashboardTheme.text_color},"& input":{color:dashboardTheme.text_color},"& textarea":{color:dashboardTheme.text_color}};
}

function Row({label,children,dashboardTheme}){
  return(
    <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"230px minmax(0,1fr)"},borderBottom:`1px solid ${dashboardTheme.border_color}`}}>
      <Box sx={{bgcolor:dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.04)":"rgba(17,24,39,0.035)",px:{xs:1.5,md:2},py:1.25,display:"flex",alignItems:"center"}}>
        <Typography sx={{fontSize:14,fontWeight:700,color:dashboardTheme.text_color}}>{label}</Typography>
      </Box>
      <Box sx={{px:{xs:1.2,md:1.4},py:1,display:"flex",alignItems:"center",minWidth:0}}>{children}</Box>
    </Box>
  );
}

export default function DetailInvoice({orden,invoiceData,updateInvoice,personOptions,personLoading,personInput,setPersonInput,establecimientos,availablePuntos,formasPago,documentoFactura,resumenFactura,onPersonCreated}){
  const{dashboardTheme}=useDashboardTheme();
  const[personModalOpen,setPersonModalOpen]=useState(false);
  const totalFactura=useMemo(()=>parseAmount(resumenFactura?.total),[resumenFactura?.total]);
  const pagos=invoiceData.pagos||[];
  const totalPagos=useMemo(()=>round2(pagos.reduce((sum,pago)=>sum+parseAmount(pago.monto),0)),[pagos]);
  const saldo=round2(Math.max(totalFactura-totalPagos,0));
  const excedente=round2(Math.max(totalPagos-totalFactura,0));
  const pagoMixto=pagos.length===2;
  const pagoEfectivo=pagos.find(pago=>pago.forma_pago==="EFECTIVO");
  const soloEfectivo=pagos.length===1&&pagoEfectivo;
  const valorRecibido=parseAmount(invoiceData.valor_recibido);
  const cambio=soloEfectivo?Math.max(valorRecibido-totalFactura,0):0;
  const valorInsuficiente=soloEfectivo&&invoiceData.valor_recibido!==""&&valorRecibido<totalFactura;
  const pagosInvalidos=totalPagos!==round2(totalFactura);
  const tipoPersonaInitialValues=useMemo(()=>({tipo:"N",es_cliente:invoiceData.tipo_persona==="CLIENTE",es_proveedor:invoiceData.tipo_persona==="PROVEEDOR",es_empleado:false,es_extranjero:false}),[invoiceData.tipo_persona]);

  const handlePersonSaved=data=>{
    const persona=data?.persona||data?.person||data?.result||data?.data||data;
    if(persona?.id){
      const selected=normalizePerson(persona);
      updateInvoice({persona:selected,documento_tipo:selected.documento_tipo,documento_valor:selected.documento_valor});
      onPersonCreated?.(selected);
    }
    setPersonModalOpen(false);
  };

  const updatePago=(index,patch)=>{
    const nuevos=pagos.map((pago,i)=>i===index?{...pago,...patch}:pago);
    updateInvoice({pagos:nuevos});
  };

  const changeFormaPago=(index,formaPago)=>{
    const nuevos=pagos.map((pago,i)=>{
      if(i!==index)return pago;
      return{...pago,forma_pago:formaPago,transferencia_codigo:""};
    });
    updateInvoice({
      pagos:nuevos,
      valor_recibido:nuevos.length===1&&formaPago==="EFECTIVO"?invoiceData.valor_recibido||nuevos[0].monto:""
    });
  };

  const addPago=()=>{
    if(pagos.length>=2)return;
    const usada=pagos[0]?.forma_pago||"EFECTIVO";
    const formaPago=usada==="EFECTIVO"?"TRANSFERENCIA":"EFECTIVO";
    const montoPrimero=parseAmount(pagos[0]?.monto);
    let primerMonto=montoPrimero;
    let segundoMonto=0;
    if(montoPrimero>=totalFactura){
      primerMonto=0;
      segundoMonto=totalFactura;
    }else{
      segundoMonto=round2(totalFactura-montoPrimero);
    }
    updateInvoice({
      pagos:[
        {...pagos[0],monto:primerMonto?String(round2(primerMonto)):""},
        {forma_pago:formaPago,monto:segundoMonto?String(segundoMonto):"",transferencia_codigo:""}
      ],
      valor_recibido:""
    });
  };

  const removePago=index=>{
    const restantes=pagos.filter((_pago,i)=>i!==index);
    if(!restantes.length)return;
    const unico={...restantes[0],monto:String(totalFactura)};
    updateInvoice({
      pagos:[unico],
      valor_recibido:unico.forma_pago==="EFECTIVO"?String(totalFactura):""
    });
  };

  const ajustarSaldo=index=>{
    const otros=pagos.reduce((sum,pago,i)=>i===index?sum:sum+parseAmount(pago.monto),0);
    updatePago(index,{monto:String(round2(Math.max(totalFactura-otros,0)))});
  };

  return(
    <>
      <Paper elevation={0} sx={{borderRadius:"14px",border:`1px solid ${dashboardTheme.border_color}`,bgcolor:dashboardTheme.card_background,overflow:"hidden",boxShadow:"none"}}>
        <Box sx={{px:{xs:1.5,md:2},py:1.4,borderBottom:`1px solid ${dashboardTheme.border_color}`,bgcolor:dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.025)":"rgba(17,24,39,0.02)"}}>
          <Typography sx={{fontSize:{xs:20,md:23},fontWeight:700,color:dashboardTheme.primary_color,lineHeight:1.2}}>Información del Documento</Typography>
        </Box>
        <Box sx={{p:{xs:1.5,md:2}}}>
          <Box sx={{borderTop:`1px solid ${dashboardTheme.border_color}`,borderLeft:`1px solid ${dashboardTheme.border_color}`,borderRight:`1px solid ${dashboardTheme.border_color}`,borderRadius:"10px",overflow:"hidden"}}>
            <Row label="Fecha de emisión" dashboardTheme={dashboardTheme}>
              <TextField size="small" type="date" value={invoiceData.fecha_emision} onChange={event=>updateInvoice({fecha_emision:event.target.value})} InputLabelProps={{shrink:true}} sx={{width:{xs:"100%",md:340},...fieldSx(dashboardTheme)}}/>
            </Row>
            <Row label="Tipo de persona" dashboardTheme={dashboardTheme}>
              <TextField select size="small" value={invoiceData.tipo_persona} onChange={event=>{updateInvoice({tipo_persona:event.target.value,persona:null,documento_tipo:"",documento_valor:""});setPersonInput("");}} sx={{width:{xs:"100%",md:340},...fieldSx(dashboardTheme)}}>
                <MenuItem value="CLIENTE">Cliente</MenuItem>
                <MenuItem value="PROVEEDOR">Proveedor</MenuItem>
              </TextField>
            </Row>
            <Row label="Establecimiento" dashboardTheme={dashboardTheme}>
              <TextField select size="small" value={invoiceData.establecimiento} onChange={event=>updateInvoice({establecimiento:event.target.value,punto_emision_id:""})} sx={{width:{xs:"100%",md:340},...fieldSx(dashboardTheme)}}>
                {establecimientos.map(item=><MenuItem key={item.id} value={item.codigo}>{item.codigo} - {item.nombre}</MenuItem>)}
              </TextField>
            </Row>
            <Row label="Punto de emisión" dashboardTheme={dashboardTheme}>
              <TextField select size="small" value={invoiceData.punto_emision_id} onChange={event=>updateInvoice({punto_emision_id:event.target.value})} sx={{width:{xs:"100%",md:340},...fieldSx(dashboardTheme)}}>
                {availablePuntos.map(item=><MenuItem key={item.id} value={item.id}>{item.establecimiento}-{item.punto_emision}</MenuItem>)}
              </TextField>
            </Row>
            <Row label="Número de documento" dashboardTheme={dashboardTheme}>
              <TextField size="small" value={documentoFactura||""} InputProps={{readOnly:true}} sx={{width:{xs:"100%",md:340},...fieldSx(dashboardTheme)}}/>
            </Row>
            <Row label="Descripción factura" dashboardTheme={dashboardTheme}>
              <TextField fullWidth size="small" multiline minRows={2} value={invoiceData.descripcion||""} onChange={event=>updateInvoice({descripcion:event.target.value})} placeholder="Descripción general de la factura" sx={fieldSx(dashboardTheme)}/>
            </Row>
            <Row label="Persona" dashboardTheme={dashboardTheme}>
              <Stack direction="row" spacing={1} sx={{width:"100%"}}>
                <Autocomplete fullWidth size="small" options={personOptions} loading={personLoading} value={invoiceData.persona} inputValue={personInput} onInputChange={(_event,newInputValue)=>setPersonInput(newInputValue)} onChange={(_event,newValue)=>{
                  const selected=normalizePerson(newValue);
                  updateInvoice({persona:selected,documento_tipo:selected?.documento_tipo||"",documento_valor:selected?.documento_valor||""});
                }} getOptionLabel={getPersonLabel} isOptionEqualToValue={(option,selected)=>String(option?.option_uid||option?.id)===String(selected?.option_uid||selected?.id)} renderOption={(props,option)=>(
                  <Box component="li" {...props} key={option.option_uid||option.id}>
                    <Box sx={{minWidth:0}}>
                      <Typography sx={{fontSize:14,fontWeight:700,color:dashboardTheme.text_color,lineHeight:1.25}}>{getPersonName(option)||"-"}</Typography>
                      <Typography sx={{mt:.25,fontSize:12.5,color:dashboardTheme.muted_text_color,lineHeight:1.25}}>{getDocumentoText(option)||"Sin documento"}</Typography>
                    </Box>
                  </Box>
                )} renderInput={params=>(
                  <TextField {...params} placeholder={invoiceData.tipo_persona==="CLIENTE"?"Buscar cliente...":"Buscar proveedor..."} sx={fieldSx(dashboardTheme)} InputProps={{...params.InputProps,endAdornment:<>{personLoading?<CircularProgress size={18}/>:null}{params.InputProps.endAdornment}</>}}/>
                )} slotProps={{paper:{sx:{bgcolor:dashboardTheme.card_background,color:dashboardTheme.text_color,border:`1px solid ${dashboardTheme.border_color}`,"& .MuiAutocomplete-option":{color:dashboardTheme.text_color},"& .MuiAutocomplete-option.Mui-focused":{bgcolor:dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.08)":"rgba(17,24,39,0.06)"}}}}}/>
                <IconButton onClick={()=>setPersonModalOpen(true)} sx={{minWidth:42,width:42,height:40,borderRadius:"10px",color:dashboardTheme.button_text_color,bgcolor:dashboardTheme.button_color,"&:hover":{bgcolor:dashboardTheme.button_hover_color}}}>
                  <AddRoundedIcon sx={{fontSize:22}}/>
                </IconButton>
              </Stack>
            </Row>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{mt:2,borderRadius:"14px",border:`1px solid ${dashboardTheme.border_color}`,bgcolor:dashboardTheme.card_background,overflow:"hidden",boxShadow:"none"}}>
        <Box sx={{px:{xs:1.5,md:2},py:1.4,borderBottom:`1px solid ${dashboardTheme.border_color}`,bgcolor:dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.025)":"rgba(17,24,39,0.02)",display:"flex",alignItems:"center",justifyContent:"space-between",gap:1,flexWrap:"wrap"}}>
          <Typography sx={{fontSize:{xs:20,md:23},fontWeight:700,color:dashboardTheme.primary_color,lineHeight:1.2}}>Información de Cobro</Typography>
          {pagos.length<2&&(
            <Button variant="outlined" size="small" startIcon={<AddRoundedIcon/>} onClick={addPago} sx={{borderRadius:"9px",textTransform:"none",fontWeight:700,borderColor:dashboardTheme.button_color,color:dashboardTheme.button_color}}>
              Añadir Método de Pago
            </Button>
          )}
        </Box>
        <Box sx={{p:{xs:1.5,md:2}}}>
          <Box sx={{borderTop:`1px solid ${dashboardTheme.border_color}`,borderLeft:`1px solid ${dashboardTheme.border_color}`,borderRight:`1px solid ${dashboardTheme.border_color}`,borderRadius:"10px",overflow:"hidden"}}>
            <Row label="Total" dashboardTheme={dashboardTheme}>
              <TextField size="small" value={`$${money(totalFactura)}`} InputProps={{readOnly:true}} sx={{width:{xs:"100%",md:340},...fieldSx(dashboardTheme),"& .MuiOutlinedInput-input":{fontWeight:700,fontSize:17}}}/>
            </Row>

            {pagos.map((pago,index)=>{
              const otraForma=pagos.find((_item,i)=>i!==index)?.forma_pago||"";
              const esTransferencia=pago.forma_pago==="TRANSFERENCIA";
              return(
                <React.Fragment key={index}>
                  <Row label={pagoMixto?`Forma de pago ${index+1}`:"Forma de pago"} dashboardTheme={dashboardTheme}>
                    <Stack direction={{xs:"column",sm:"row"}} spacing={1} sx={{width:{xs:"100%",md:520}}}>
                      <TextField select size="small" value={pago.forma_pago} onChange={event=>changeFormaPago(index,event.target.value)} sx={{width:{xs:"100%",sm:240},...fieldSx(dashboardTheme)}}>
                        {formasPago.map(item=><MenuItem key={item.value} value={item.value} disabled={pagos.length===2&&otraForma===item.value}>{item.label}</MenuItem>)}
                      </TextField>
                      <TextField size="small" type="number" label="Monto" value={pago.monto} onChange={event=>updatePago(index,{monto:event.target.value})} inputProps={{min:"0",step:"0.01",inputMode:"decimal"}} sx={{width:{xs:"100%",sm:180},...fieldSx(dashboardTheme)}}/>
                      {pagos.length===2&&(
                        <IconButton onClick={()=>removePago(index)} sx={{width:40,height:40,borderRadius:"9px",color:"#dc2626",border:`1px solid ${dashboardTheme.border_color}`}}>
                          <DeleteOutlineRoundedIcon/>
                        </IconButton>
                      )}
                    </Stack>
                  </Row>
                  {pagos.length===2&&(
                    <Row label={`Completar pago ${index+1}`} dashboardTheme={dashboardTheme}>
                      <Button variant="text" onClick={()=>ajustarSaldo(index)} sx={{textTransform:"none",fontWeight:700,color:dashboardTheme.button_color}}>Usar saldo restante</Button>
                    </Row>
                  )}
                  {esTransferencia&&(
                    <Row label={pagoMixto?`Código transferencia ${index+1}`:"Código transferencia"} dashboardTheme={dashboardTheme}>
                      <TextField size="small" value={pago.transferencia_codigo||""} onChange={event=>updatePago(index,{transferencia_codigo:event.target.value})} sx={{width:{xs:"100%",md:340},...fieldSx(dashboardTheme)}}/>
                    </Row>
                  )}
                </React.Fragment>
              );
            })}

            {soloEfectivo&&(
              <>
                <Row label="Recibido" dashboardTheme={dashboardTheme}>
                  <TextField autoFocus size="small" type="number" value={invoiceData.valor_recibido} onChange={event=>updateInvoice({valor_recibido:event.target.value})} inputProps={{min:"0",step:"0.01",inputMode:"decimal"}} error={valorInsuficiente} sx={{width:{xs:"100%",md:340},...fieldSx(dashboardTheme),"& .MuiOutlinedInput-input":{fontSize:17,fontWeight:700}}}/>
                </Row>
                <Row label="Cambio" dashboardTheme={dashboardTheme}>
                  <TextField size="small" value={`$${money(cambio)}`} InputProps={{readOnly:true}} sx={{width:{xs:"100%",md:340},...fieldSx(dashboardTheme),"& .MuiOutlinedInput-root":{borderRadius:"8px",bgcolor:valorInsuficiente?(dashboardTheme.theme_mode==="dark"?"rgba(220,38,38,0.12)":"rgba(220,38,38,0.06)"):(dashboardTheme.theme_mode==="dark"?"rgba(34,197,94,0.12)":"rgba(34,197,94,0.07)")},"& .MuiOutlinedInput-input":{fontSize:18,fontWeight:700,color:valorInsuficiente?"#dc2626":dashboardTheme.text_color}}}/>
                </Row>
              </>
            )}

            {pagoMixto&&(
              <>
                <Row label="Total pagado" dashboardTheme={dashboardTheme}>
                  <TextField size="small" value={`$${money(totalPagos)}`} InputProps={{readOnly:true}} error={pagosInvalidos} sx={{width:{xs:"100%",md:340},...fieldSx(dashboardTheme)}}/>
                </Row>
                <Row label={excedente>0?"Excedente":"Saldo pendiente"} dashboardTheme={dashboardTheme}>
                  <TextField size="small" value={`$${money(excedente>0?excedente:saldo)}`} InputProps={{readOnly:true}} error={pagosInvalidos} sx={{width:{xs:"100%",md:340},...fieldSx(dashboardTheme),"& .MuiOutlinedInput-input":{fontWeight:700,color:pagosInvalidos?"#dc2626":dashboardTheme.text_color}}}/>
                </Row>
              </>
            )}
          </Box>
        </Box>
      </Paper>
      <Persons open={personModalOpen} person={null} initialValues={tipoPersonaInitialValues} onClose={()=>setPersonModalOpen(false)} onSaved={handlePersonSaved}/>
    </>
  );
}